import { cn } from "@/lib/utils"

interface SoundCardSkeletonProps {
  className?: string
}

/**
 * Skeleton loading placeholder for SoundCard (components/ui/sound-card.tsx).
 * Mirrors the resting 68px/76px ruled row so loading never flashes a tile.
 * Uses --tls-* tokens (not the legacy --color-* aliases) to match the real
 * card's surfaces exactly in both themes.
 */
export function SoundCardSkeleton({ className }: SoundCardSkeletonProps) {
  return (
    // §02.5: skeletons do NOT animate — flat color-hairline fill, in every
    // theme and every motion preference. §12.8 amends the pre-existing
    // shimmer/opacity-pulse pattern this file used, which was decorative
    // motion the system had already banned elsewhere.
    <div
      aria-hidden="true"
      data-testid="sound-card-skeleton"
      className={cn("grid", className)}
      style={{
        gridTemplateColumns: "44px minmax(0, 1fr)",
        gap: 16,
        alignItems: "center",
        minHeight: 68,
        padding: "0 16px",
        background: "transparent",
        borderBottom: "1px solid var(--tls-border-subtle)",
      }}
    >
      <div
        className="rounded-full"
        style={{
          width: 44,
          height: 44,
          background: "var(--tls-surface-hover)",
        }}
      />
      <div
        style={{ height: 16, width: "min(34rem, 72%)", background: "var(--tls-surface-hover)" }}
      />
    </div>
  )
}

interface SoundCardSkeletonGridProps {
  count?: number
  className?: string
}

/**
 * Ruled skeleton index for initial loading.
 */
export function SoundCardSkeletonGrid({ count = 12, className }: SoundCardSkeletonGridProps) {
  const skeletonKeys = Array.from({ length: count }, (_, index) => `sound-card-skeleton-${index}`)

  return (
    <div className={cn(
      "grid grid-cols-1 gap-0",
      className
    )}>
      {skeletonKeys.map((key) => (
        <SoundCardSkeleton key={key} />
      ))}
    </div>
  )
}
